import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { APPLICATIONS_ROUTE, PROJECTS_ROUTE, PUBLIC_MODULES_ROUTE, USER_MODULES_ROUTE } from 'src/app/shared/constants/constants';
import { BreadcrumbPart } from '../../models/breadcrumb-part.model';
import { ExperimentalFeaturesService } from '../experimental-features.service';

@Injectable({
    providedIn: 'root',
})
export class BreadcrumbAndTitleService {
    private breadcrumbParts$$ = new BehaviorSubject<BreadcrumbPart[]>([]);
    private readonly titleSuffix = 'arivis Cloud';
    private readonly MEDTitleSuffix = 'ZEISS RDP';
    private readonly titleSeparator = ' - ';

    constructor(
        private titleService: Title,
        private translateService: TranslateService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
    ) {}

    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    get breadcrumbs$(): Observable<BreadcrumbPart[]> {
        return this.breadcrumbParts$$.asObservable().pipe(
            map((breadcrumbParts: BreadcrumbPart[]): BreadcrumbPart[] => {
                if (breadcrumbParts.length > 0) {
                    // The Title is in reverse parts. So, slice the array (to not manipulate the original) and reverse it.
                    const reversedBcParts = breadcrumbParts
                        .slice()
                        .reverse()
                        .map((bcPart: BreadcrumbPart): BreadcrumbPart => {
                            return bcPart;
                        });

                    forkJoin(this.getLabelsStreams(reversedBcParts))
                        .pipe(
                            map((keys: string[]): string => {
                                return keys.join(this.titleSeparator);
                            }),
                            map((title: string): void => {
                                const environmentSuffix = this.isMEDProjectEnabled
                                    ? this.titleSeparator + this.MEDTitleSuffix
                                    : this.titleSeparator + this.titleSuffix;
                                this.titleService.setTitle(title + environmentSuffix);
                            }),
                        )
                        .subscribe();
                } else {
                    this.titleService.setTitle(this.isMEDProjectEnabled ? this.MEDTitleSuffix : this.titleSuffix);
                }
                return breadcrumbParts;
            }),
        );
    }

    setBreadcrumbAndTitle(bcParts: BreadcrumbPart[]): void {
        this.breadcrumbParts$$.next(bcParts);
    }

    appendBreadcrumbItem(breadcrumbPart: BreadcrumbPart): this {
        this.breadcrumbParts$$.next([...this.breadcrumbParts$$.value, breadcrumbPart]);
        return this;
    }

    setBreadcrumbToAdminSpace(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: '/admin',
                titleKey: 'ADMINISTRATION.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToAdminArticlesSpace(): BreadcrumbAndTitleService {
        this.setBreadcrumbToAdminSpace().appendBreadcrumbItem({
            route: 'admin/articles',
            titleKey: 'ARTICLES.BROWSER_TITLE',
        });

        return this;
    }

    setBreadcrumbToPublicModules(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: PUBLIC_MODULES_ROUTE,
                titleKey: 'MODULES.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToUserModules(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: USER_MODULES_ROUTE,
                titleKey: 'MODULES.MY_MODULES.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToSubmittedModules(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: '/admin/submitted-modules',
                titleKey: 'ADMINISTRATION.SUBMITTED_MODULES.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToUserResults(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: '/user/workspace/results',
                titleKey: 'MY_WORKSPACE.MY_RESULTS.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToPublicResults(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: '/results',
                titleKey: 'RESULTS.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToUserWorkflows(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: '/user/workspace/workflows',
                titleKey: 'WORKFLOWS.MY_WORKFLOWS.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToPublicWorkflows(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: '/workflows',
                titleKey: 'WORKFLOWS.BROWSER_TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToApplications(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: APPLICATIONS_ROUTE,
                titleKey: 'APPLICATIONS.TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToProjects(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                route: PROJECTS_ROUTE,
                titleKey: 'PROJECTS.TITLE',
            },
        ]);

        return this;
    }

    setBreadcrumbToAnnotate(): BreadcrumbAndTitleService {
        this.setBreadcrumbAndTitle([
            {
                titleKey: 'MACHINE_LEARNING.ANNOTATE.TITLE',
            },
        ]);

        return this;
    }

    private getLabelsStreams(reversedBcParts: BreadcrumbPart[]): Observable<string>[] {
        return reversedBcParts.map((bcPart: BreadcrumbPart) => {
            return this.translateService.get(bcPart.titleKey, bcPart.titleParams);
        });
    }
}
